import React, { useMemo, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Paper } from '@mui/material'
import { parse } from 'qs'

import { TableUrlPagination } from '../../../../ui'
import { useCombos } from '../../../combos'
import { useAssociatesIP3 } from '../../contexts'
import { ASSOCIATES_IP3_TABLE_COLUMNS, ASSOCIATES_IP3_TABLE_STATES } from '../../constants'
import { ASSOCIATES_IP3_COMBOS } from '../../constants'
import { DEFAULT_QUERY } from '../../reducers'
import { TableActions } from '../table-actions'
import { associatesIP3TableStyles } from './associates-ip3-table.styles'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const AssociatesIP3Table = () => {
  const { formatMessage, formatDate } = useIntl()
  const combos = useCombos(ASSOCIATES_IP3_COMBOS)
  const location = useLocation()
  const {
    data,
    pagination: { total_solicitudes: totalEntries },
    loading,
    search,
    getTableData,
  } = useAssociatesIP3()
  const global = useSelector((state) => state.global)

  useEffect(() => {
    const urlSearchParams = parse(location.search.split('?')[1])
    const searchParams = {
      ...DEFAULT_QUERY,
      ...urlSearchParams,
    }
    getTableData(searchParams, global.gremio_id)
  }, [location.search])

  const formattedColumns = useMemo(() => {
    const columns = [...ASSOCIATES_IP3_TABLE_COLUMNS]

    const actionColumn = {
      field: 'actions',
      width: 110,
      align: 'center',
      sorting: false,
    }
    columns.push(actionColumn)

    return columns.map((column) => {
      const baseColumn = {
        accessorKey: column.field,
        id: column.field,
        size: column.width,
        minSize: column.minWidth || column.width,
        maxSize: column.maxWidth,
        enableSorting: column.sorting !== false,
        muiTableHeadCellProps: {
          align: column.align || 'left',
        },
        muiTableBodyCellProps: {
          align: column.align || 'left',
          sx: {
            minWidth: column.minWidth || column.width,
          },
        },
      }

      if (column.field === 'actions') {
        return {
          ...baseColumn,
          header: formatMessage({ id: 'global.actions' }),
          Cell: ({ row }) => <TableActions row={row} />,
        }
      }

      const result = {
        ...baseColumn,
        header: formatMessage({ id: `pages.guilds.table.columns.${column.field}.title` }),
      }

      if (
        column.hasOwnProperty('comboId') &&
        combos[column.comboId] &&
        combos[column.comboId].data
      ) {
        const lookup = getLookup(combos[column.comboId].data)
        result.Cell = ({ cell }) => {
          const value = cell.getValue()
          return lookup[value] || value || <span>--</span>
        }
      } else {
        result.Cell = ({ cell }) => {
          const value = cell.getValue()
          return value || <span>--</span>
        }
      }

      return result
    })
  }, [combos, formatMessage])

  const processedData = useMemo(
    () =>
      data.map((item) => {
        const formattedDates = {}
        const formattedStatus = {}
        ASSOCIATES_IP3_TABLE_COLUMNS.forEach((column) => {
          if (column.type === 'date' && item[column.field]) {
            formattedDates[column.field] = formatDate(item[column.field])
          }
          if (column.field === 'aceptacion_gremio') {
            formattedStatus[column.field] = item[column.field]
              ? ASSOCIATES_IP3_TABLE_STATES.accepted
              : ASSOCIATES_IP3_TABLE_STATES.pending
          }
          if (column.field === 'gremio_recibe_demandas') {
            formattedStatus[column.field] = item[column.field]
              ? ASSOCIATES_IP3_TABLE_STATES.yes
              : ASSOCIATES_IP3_TABLE_STATES.no
          }
        })
        return {
          ...item,
          ...formattedDates,
          ...formattedStatus,
          tableData: {
            ...item.tableData,
          },
        }
      }),
    [data]
  )

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: false,
      tableLayout: 'fixed',
      fixedColumns: {
        right: 1,
      },
      draggable: false,
      muiTableProps: {
        sx: {
          tableLayout: 'fixed',
        },
      },
    }),
    []
  )

  return (
    <TableUrlPagination
      key={`associates-table-${search['_pagina'] || 1}-${search['_num_x_pagina'] || 5}`}
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      muiTableBodyRowProps={({ row }) => {
        const isSuccess = row.original.aceptacion_gremio === ASSOCIATES_IP3_TABLE_STATES.accepted

        return {
          sx: {
            '& .MuiTableCell-root, & .MuiDataGrid-cell': {
              backgroundColor: isSuccess
                ? associatesIP3TableStyles.success.backgroundColor
                : associatesIP3TableStyles.failure.backgroundColor,
            },
          },
        }
      }}
    />
  )
}
